// Конструктор отчётов: каталоги, вычисление значений виджетов.
import { computeSummary } from "./summaryRules";

export const WIDGET_TYPES = [
  { key: "kpi", label: "KPI (число)" },
  { key: "group", label: "Группировка" },
  { key: "dynamics", label: "Динамика по дате" },
  { key: "actions", label: "Распределение по действиям" }
];

export const AGGREGATIONS = [
  { key: "sum", label: "Сумма" },
  { key: "avg", label: "Среднее" },
  { key: "count", label: "Количество" },
  { key: "min", label: "Минимум" },
  { key: "max", label: "Максимум" }
];

export const METRICS = [
  { key: "sum", label: "Сумма" },
  { key: "count", label: "Количество" }
];

export const BUCKETS = [
  { key: "day", label: "По дням" },
  { key: "week", label: "По неделям" },
  { key: "month", label: "По месяцам" }
];

export const CHARTS = [
  { key: "bar", label: "Бар-чарт" },
  { key: "table", label: "Таблица" }
];

const COLORS = ["#f59e0b", "#2563eb", "#16a34a", "#dc2626", "#7c3aed", "#0891b2"];

const FLAT_KEYS = ["contractor", "branch", "purpose", "initiator"];

const toNum = (value) => {
  const n = Number(String(value).replace(",", ".").trim());
  return Number.isNaN(n) ? 0 : n;
};

// Поля, доступные для выбора в виджетах.
export function getReportFields(templateColumns = []) {
  const fields = templateColumns.map((column) => ({
    key: column.key || column.id,
    label: column.label,
    isNumber: column.key === "amount" || column.format === "number",
    isDate: column.format === "date"
  }));

  if (!fields.some((f) => f.key === "amount")) {
    fields.unshift({ key: "amount", label: "Сумма", isNumber: true, isDate: false });
  }

  fields.push({ key: "status", label: "Статус", isNumber: false, isDate: false });

  return fields;
}

export function getDateFields(templateColumns = []) {
  return templateColumns
    .filter((column) => column.format === "date")
    .map((column) => ({ key: column.key || column.id, label: column.label }));
}

// Значение поля платежа (учитывает плоские поля, extra_fields и двухуровневый режим).
export function getPaymentValue(payment, fieldKey, templateColumns = []) {
  if (fieldKey === "amount") return Number(payment.amount || 0);
  if (fieldKey === "status") return payment.status || "";

  const items = Array.isArray(payment.items) ? payment.items : [];

  if (items.length > 0) {
    const column = templateColumns.find((c) => (c.key || c.id) === fieldKey);
    if (column) {
      const rule = column.key === "amount" || column.format === "number" ? "sum" : "same";
      const result = computeSummary(column, rule, items);
      return result ? result.value : "";
    }
  }

  if (FLAT_KEYS.includes(fieldKey)) return payment[fieldKey] || "";
  return payment.extra_fields?.[fieldKey] ?? "";
}

function aggregate(values, agg) {
  if (agg === "count") return values.length;

  const nums = values.map(toNum);
  if (nums.length === 0) return 0;

  if (agg === "avg") return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === "min") return Math.min(...nums);
  if (agg === "max") return Math.max(...nums);
  return nums.reduce((a, b) => a + b, 0); // sum
}

function weekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function bucketize(dateStr, bucket) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr));
  if (!match) return null;

  const [, y, mo, d] = match;
  if (bucket === "month") return `${y}-${mo}`;
  if (bucket === "week") return weekKey(new Date(`${y}-${mo}-${d}T00:00:00Z`));
  return `${y}-${mo}-${d}`;
}

// Готовит данные виджета для рендера.
export function computeWidget(widget, payments, meta = {}) {
  const { templateColumns = [], paymentActions = [], actionColumns = [] } = meta;

  if (widget.type === "kpi") {
    let rows = payments;

    if (widget.filter && widget.filter.by) {
      rows = rows.filter(
        (p) => String(getPaymentValue(p, widget.filter.by, templateColumns)) === String(widget.filter.value)
      );
    }

    const field = widget.field || "amount";
    const values = rows.map((p) => getPaymentValue(p, field, templateColumns));

    return { kind: "kpi", value: aggregate(values, widget.agg || "sum"), count: rows.length };
  }

  if (widget.type === "group") {
    const metric = widget.metric || "sum";
    const map = {};

    payments.forEach((p) => {
      const key = String(getPaymentValue(p, widget.groupBy, templateColumns) || "Не указано");
      if (!map[key]) map[key] = { label: key, amount: 0, count: 0 };
      map[key].amount += toNum(getPaymentValue(p, "amount", templateColumns));
      map[key].count += 1;
    });

    let rows = Object.values(map).sort((a, b) =>
      metric === "count" ? b.count - a.count : b.amount - a.amount
    );

    if (widget.topN) rows = rows.slice(0, widget.topN);

    const max = Math.max(...rows.map((r) => (metric === "count" ? r.count : r.amount)), 1);
    return { kind: "group", rows, metric, max, chart: widget.chart || "bar" };
  }

  if (widget.type === "dynamics") {
    const metric = widget.metric || "sum";
    const map = {};

    payments.forEach((p) => {
      const raw = getPaymentValue(p, widget.dateField, templateColumns);
      const key = raw ? bucketize(raw, widget.bucket || "day") : null;
      if (!key) return;
      if (!map[key]) map[key] = { key, amount: 0, count: 0 };
      map[key].amount += toNum(getPaymentValue(p, "amount", templateColumns));
      map[key].count += 1;
    });

    const rows = Object.values(map).sort((a, b) => a.key.localeCompare(b.key)).slice(-12);
    const max = Math.max(...rows.map((r) => (metric === "count" ? r.count : r.amount)), 1);
    return { kind: "dynamics", rows, metric, max };
  }

  if (widget.type === "actions") {
    let metaList;

    if (widget.source && widget.source !== "status") {
      const column = actionColumns.find((c) => c.id === widget.source);
      metaList = column
        ? column.options.map((o, i) => ({
            columnId: column.id, value: o.value, label: o.label, color: COLORS[i % COLORS.length]
          }))
        : [];
    } else {
      metaList = paymentActions.map((a, i) => ({
        status: a.status, label: a.label, color: COLORS[i % COLORS.length]
      }));
    }

    const total = payments.length || 1;
    const rows = metaList.map((m) => {
      const matched = payments.filter((p) =>
        m.columnId ? p.extra_fields?.[m.columnId] === m.value : p.status === m.status
      );
      return {
        ...m,
        count: matched.length,
        amount: matched.reduce((s, p) => s + toNum(p.amount), 0),
        percent: Math.round((matched.length / total) * 100)
      };
    });

    return { kind: "actions", rows };
  }

  return null;
}

let widgetSeq = 0;
export const newWidget = (type) => {
  widgetSeq += 1;
  const id = `w_${Date.now()}_${widgetSeq}`;
  const base = { id, type, title: WIDGET_TYPES.find((t) => t.key === type)?.label || "Виджет" };

  if (type === "kpi") return { ...base, field: "amount", agg: "sum" };
  if (type === "group") return { ...base, groupBy: "contractor", metric: "sum", topN: 5, chart: "bar" };
  if (type === "dynamics") return { ...base, dateField: "", bucket: "day", metric: "sum" };
  if (type === "actions") return { ...base, source: "status" };
  return base;
};

// Пресет по умолчанию — повторяет прежнюю страницу.
export const DEFAULT_WIDGETS = () => ([
  { id: "w_total", type: "kpi", title: "Общая сумма", field: "amount", agg: "sum" },
  { id: "w_avg", type: "kpi", title: "Средний платеж", field: "amount", agg: "avg" },
  { id: "w_count", type: "kpi", title: "Количество платежей", field: "amount", agg: "count" },
  { id: "w_actions", type: "actions", title: "Распределение по действиям", source: "status" },
  { id: "w_contractors", type: "group", title: "Крупные контрагенты", groupBy: "contractor", metric: "sum", topN: 5, chart: "bar" },
  { id: "w_branches", type: "group", title: "Города по объему платежей", groupBy: "branch", metric: "sum", topN: 5, chart: "bar" }
]);
